import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./adminFeatureTour.css";

const SPOTLIGHT_PAD = 10;
const CARD_GAP = 14;
const VIEW_MARGIN = 12;
const CARD_WIDTH = 400;
const CARD_FALLBACK_H = 190;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/** Exact target bounds + pad, clipped to the viewport (no artificial shift/cap). */
function getSpotlightRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top = r.top - SPOTLIGHT_PAD;
  let left = r.left - SPOTLIGHT_PAD;
  let right = r.right + SPOTLIGHT_PAD;
  let bottom = r.bottom + SPOTLIGHT_PAD;

  top = clamp(top, VIEW_MARGIN, vh - VIEW_MARGIN);
  left = clamp(left, VIEW_MARGIN, vw - VIEW_MARGIN);
  right = clamp(right, VIEW_MARGIN, vw - VIEW_MARGIN);
  bottom = clamp(bottom, VIEW_MARGIN, vh - VIEW_MARGIN);

  const width = Math.max(40, right - left);
  const height = Math.max(40, bottom - top);
  if (width < 40 || height < 40) return null;

  return { top, left, width, height, right: left + width, bottom: top + height };
}

function overlapArea(a, b) {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

/**
 * Pick card position: right → left → bottom → top, aligned to the spotlight.
 * When the target fills the viewport so every side overlaps, fall back to the
 * viewport corner that covers the least of the highlight.
 */
function getCardPosition(spot, cardW, cardH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const m = VIEW_MARGIN;
  const centerTop = spot.top + spot.height / 2 - cardH / 2;
  const centerLeft = spot.left + spot.width / 2 - cardW / 2;

  const candidates = [
    { side: "right", top: centerTop, left: spot.right + CARD_GAP, space: vw - spot.right - m, bonus: 40 },
    { side: "left", top: centerTop, left: spot.left - CARD_GAP - cardW, space: spot.left - m, bonus: 30 },
    { side: "bottom", top: spot.bottom + CARD_GAP, left: centerLeft, space: vh - spot.bottom - m, bonus: 20 },
    { side: "top", top: spot.top - CARD_GAP - cardH, left: centerLeft, space: spot.top - m, bonus: 0 },
    { side: "fallback", top: vh - cardH - m, left: vw - cardW - m, space: 0, bonus: -10 },
    { side: "fallback", top: vh - cardH - m, left: m, space: 0, bonus: -20 },
    { side: "fallback", top: m, left: vw - cardW - m, space: 0, bonus: -30 },
    { side: "fallback", top: m, left: m, space: 0, bonus: -40 },
  ];

  let best = null;
  let bestScore = -Infinity;

  for (const c of candidates) {
    const left = clamp(c.left, m, vw - cardW - m);
    const top = clamp(c.top, m, vh - cardH - m);
    const cardRect = { top, left, right: left + cardW, bottom: top + cardH };

    // A side that fits cleanly always beats one that covers the highlight;
    // among overlapping ones, cover as little of it as possible.
    const covered = overlapArea(cardRect, spot);
    const needed = c.side === "left" || c.side === "right" ? cardW : cardH;
    const fits = covered === 0 && (c.side === "fallback" || c.space >= needed + CARD_GAP);
    const score = fits ? 1e9 + c.space + c.bonus : -covered + c.bonus;

    if (score > bestScore) {
      bestScore = score;
      best = { top, left, side: c.side };
    }
  }

  return best || { top: m, left: m, side: "fallback" };
}

function scrollTargetIntoView(el) {
  if (!el) return;
  // Targets taller than the viewport are scrolled to their top so the heading stays visible.
  const tall = el.getBoundingClientRect().height + SPOTLIGHT_PAD * 2 > window.innerHeight - VIEW_MARGIN * 2;
  try {
    el.scrollIntoView({ behavior: "smooth", block: tall ? "start" : "center", inline: "nearest" });
  } catch {
    const parent =
      el.closest(".rules-php-card") ||
      el.closest(".themeupdate-page") ||
      el.parentElement;
    if (parent) parent.scrollTop = Math.max(0, el.offsetTop - 80);
  }
}

/**
 * Shared admin feature tour.
 * Spotlight matches the target; description card docks left/right/top/bottom to avoid overlap.
 */
export default function AdminFeatureTour({ open, onClose, steps = [], onStepChange }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardPos, setCardPos] = useState(null);
  const cardRef = useRef(null);

  const step = steps[stepIndex];
  const isLast = stepIndex >= steps.length - 1;

  const goNext = useCallback(() => {
    if (isLast) onClose?.();
    else setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  }, [isLast, onClose, steps.length]);

  const goBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const placeCard = useCallback((spot) => {
    if (!spot) {
      setCardPos(null);
      return;
    }
    const cardEl = cardRef.current;
    const cardW = Math.min(CARD_WIDTH, window.innerWidth - VIEW_MARGIN * 2);
    const cardH = cardEl?.offsetHeight || CARD_FALLBACK_H;
    setCardPos(getCardPosition(spot, cardW, cardH));
  }, []);

  /** Re-read the target's current position without scrolling. */
  const track = useCallback(() => {
    if (!open || !step) return;
    const spot = getSpotlightRect(document.querySelector(`[data-tour="${step.target}"]`));
    setRect(spot);
    // Place after paint so cardRef has real height
    window.requestAnimationFrame(() => placeCard(spot));
  }, [open, step, placeCard]);

  useLayoutEffect(() => {
    if (!open || !step) {
      setRect(null);
      setCardPos(null);
      return undefined;
    }
    onStepChange?.(step, stepIndex);
    const timers = [];
    const delay = step.tab ? 280 : 80;
    timers.push(
      window.setTimeout(() => {
        scrollTargetIntoView(document.querySelector(`[data-tour="${step.target}"]`));
        track();
        // Smooth scrolling can outlast the scroll events on some browsers; settle once more.
        timers.push(window.setTimeout(track, 450));
      }, delay)
    );

    // Follow the target while it scrolls (any scroll container) or the layout shifts.
    let frame = 0;
    const onMove = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        track();
      });
    };
    window.addEventListener("resize", onMove);
    document.addEventListener("scroll", onMove, true);
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", onMove);
      document.removeEventListener("scroll", onMove, true);
    };
  }, [open, stepIndex, step, track, onStepChange]);

  // Re-place card when spotlight updates (after first render with content)
  useLayoutEffect(() => {
    if (!rect) return;
    placeCard(rect);
  }, [rect, stepIndex, placeCard]);

  useEffect(() => {
    if (!open) return undefined;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStepIndex(0);
      setRect(null);
      setCardPos(null);
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        goNext();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goBack();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, goNext, goBack, onClose]);

  if (!open || !step || !steps.length) return null;

  const cardStyle = cardPos
    ? { top: cardPos.top, left: cardPos.left }
    : { top: VIEW_MARGIN, left: VIEW_MARGIN, visibility: "hidden" };

  return (
    <div className="admin-tour" role="dialog" aria-modal="true" aria-labelledby="admin-tour-title">
      <div className="admin-tour__shade" aria-hidden />

      {rect ? (
        <div
          className="admin-tour__spotlight"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
          aria-hidden
        />
      ) : null}

      <div
        ref={cardRef}
        className={`admin-tour__card${cardPos?.side ? ` admin-tour__card--${cardPos.side}` : ""}`}
        style={cardStyle}
      >
        <div className="admin-tour__meta">
          Step {stepIndex + 1} of {steps.length}
        </div>
        <h3 id="admin-tour-title" className="admin-tour__title">
          {step.title}
        </h3>
        <p className="admin-tour__body">{step.body}</p>
        <div className="admin-tour__actions">
          <button type="button" className="admin-tour__btn admin-tour__btn--ghost" onClick={onClose}>
            Skip
          </button>
          <div className="admin-tour__nav">
            {stepIndex > 0 ? (
              <button
                type="button"
                className="admin-tour__btn admin-tour__btn--secondary"
                onClick={goBack}
              >
                Back
              </button>
            ) : null}
            <button type="button" className="admin-tour__btn admin-tour__btn--primary" onClick={goNext}>
              {isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function markTourSeen(storageKey) {
  try {
    localStorage.setItem(storageKey, "1");
  } catch {
    /* ignore */
  }
}

export function shouldAutoStartTour(storageKey) {
  try {
    return localStorage.getItem(storageKey) !== "1";
  } catch {
    return false;
  }
}
